import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { setReturnUrl } from '../../lib/storage';
import { Spinner } from '../ui';

/**
 * ProtectedRoute
 *
 * Route guard component that handles authentication requirements.
 * Supports multiple levels of protection:
 * - requireAuth: User must be logged in
 * - requireEmailVerified: User must have verified their email
 * - requirePhoneVerified: User must have verified their phone
 * - requireAdmin: User must have isAdmin=true in their profile
 */
export default function ProtectedRoute({
  children,
  requireAuth = true,
  requireEmailVerified = false,
  requirePhoneVerified = false,
  requireAdmin = false,
}) {
  const {
    user,
    userProfile,
    loading,
    isAuthenticated,
    isEmailVerified,
    isPhoneVerified,
  } = useAuth();

  const location = useLocation();

  // Show loading while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Check authentication
  if (requireAuth && !isAuthenticated) {
    // Store current location for redirect after login
    setReturnUrl(location.pathname + location.search);
    return <Navigate to="/auth/login" replace />;
  }

  // Check email verification
  if (requireEmailVerified && !isEmailVerified) {
    return <Navigate to="/auth/verify-email" replace />;
  }

  // Check phone verification
  if (requirePhoneVerified && !isPhoneVerified) {
    return <Navigate to="/auth/verify-phone" replace />;
  }

  // Check admin access
  if (requireAdmin && !userProfile?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * GuestRoute
 *
 * Opposite of ProtectedRoute - only allows unauthenticated users.
 * Redirects authenticated users based on verification status:
 * - If email not verified -> verify-email
 * - If phone not verified -> verify-phone
 * - Otherwise -> home or return URL
 */
export function GuestRoute({ children, redirectTo = '/' }) {
  const { loading, isAuthenticated, isEmailVerified, isPhoneVerified } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Don't auto-redirect from signup/login pages - let the page handlers manage navigation
    // This prevents the "home flash" race condition where GuestRoute redirects before
    // the page's navigate() call executes
    const isAuthFormPage = location.pathname === '/auth/signup' ||
                           location.pathname === '/auth/login';
    if (isAuthFormPage) {
      return children;
    }

    // Check verification status and redirect appropriately
    // This prevents the "home flash" issue
    if (!isEmailVerified) {
      return <Navigate to="/auth/verify-email" replace />;
    }

    if (!isPhoneVerified) {
      return <Navigate to="/auth/verify-phone" replace />;
    }

    // Fully verified - go to intended destination
    const safeRedirect = redirectTo.startsWith('/auth/') ? '/' : redirectTo;
    return <Navigate to={safeRedirect} replace />;
  }

  return children;
}
